import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Badge, Empty } from "../components/UI";
import { ArrowLeft, Check, Play, Square, MessageSquare, History, Send, Repeat, Clock3, Flame, Pencil } from "lucide-react";
import { toast } from "sonner";

const fmtHMS = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

function StatCard({ label, value, sub, tone = "" }) {
  return (
    <Card className={`p-4 ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{label}</div>
      <div className="mt-0.5 font-heading text-xl font-bold text-[#3E2723]">{value}</div>
      {sub && <div className="text-[11px] text-[#7B6157]">{sub}</div>}
    </Card>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || { url: "/tasks", label: "Tasks" };
  const goBack = () => navigate(returnTo.url);
  const [task, setTask] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activity, setActivity] = useState(null);
  const [statusMeta, setStatusMeta] = useState({});
  const [comment, setComment] = useState("");

  const load = () => api.get(`/tasks/${id}`).then((r) => setTask(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    load();
    api.get(`/tasks/${id}/activity`).then((r) => setActivity(r.data.events)).catch(() => setActivity([]));
    api.get("/task-statuses").then((r) => setStatusMeta(Object.fromEntries(r.data.map((s) => [s.key, s])))).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (notFound) return (
    <div className="py-16 text-center" data-testid="task-detail-notfound">
      <p className="text-sm text-[#7B6157]">This task could not be found.</p>
      <Btn className="mt-4" onClick={goBack}>Back to {returnTo.label}</Btn>
    </div>
  );
  if (!task) return <div className="py-16 text-center text-sm text-[#84675B]">Loading task…</div>;

  const m = statusMeta[task.status];
  const done = (m ? m.behavior === "done" : ["completed", "archived"].includes(task.status));
  const overdue = !done && task.due_date && task.due_date < dayjs().format("YYYY-MM-DD");
  const overdueDays = overdue ? dayjs().startOf("day").diff(dayjs(task.due_date), "day") : 0;
  const daysOpen = task.created_at ? dayjs().diff(dayjs(task.created_at), "day") : 0;

  const complete = async () => {
    try { await api.put(`/tasks/${id}`, { status: "completed" }); toast.success("Task completed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const timer = async () => {
    try { const r = await api.post(`/tasks/${id}/timer/${task.timer_started_at ? "stop" : "start"}`); setTask(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tasks/${id}/comments`, { text: comment });
      setComment(""); toast.success("Comment added"); load();
      api.get(`/tasks/${id}/activity`).then((r) => setActivity(r.data.events)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };

  const details = [
    ["Client", task.client_name ? (task.client_id ? <Link to={`/clients/${task.client_id}/console`} className="font-semibold text-[#4E342E] hover:underline" data-testid="task-detail-client-link">{task.client_name}</Link> : task.client_name) : "-"],
    ["Project", task.project || "-"], ["Module", task.module || "-"],
    ["Assigned to", task.assigned_to_name || "Unassigned"], ["Owned by", task.owned_by_name || "-"],
    ["Start date", task.start_date ? dayjs(task.start_date).format("MM/DD/YYYY") : "-"],
    ["Due date", task.due_date ? dayjs(task.due_date).format("MM/DD/YYYY") : "-"],
    ["Estimated", task.estimated_hours ? `${task.estimated_hours} hours` : "-"],
    ["Milestone", task.milestone || "-"],
    ["Recurrence", task.recurrence?.enabled ? <span className="inline-flex items-center gap-1"><Repeat size={12} /> {task.recurrence.frequency}</span> : "None"],
    ["Followers", (task.followed_by_names || []).join(", ") || "-"],
    ["Created", task.created_at ? dayjs(task.created_at).format("MM/DD/YYYY h:mm A") : "-"],
  ];

  return (
    <div data-testid="task-detail-page">
      <button data-testid="task-detail-back" onClick={goBack}
        className="mb-3 flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#4E342E]"><ArrowLeft size={13} /> Back to {returnTo.label}</button>
      <PageHeader title={`Task #${task.number}`} subtitle={task.title}
        action={
          <div className="flex flex-wrap gap-2">
            <Btn variant="outline" data-testid="task-detail-edit-btn" onClick={() => navigate(`/tasks?open=${task.id}`)}><Pencil size={13} /> Edit on Board</Btn>
            {!done && <Btn data-testid="task-detail-complete-btn" className="bg-[#2E7D32] hover:bg-[#1B5E20]" onClick={complete}><Check size={14} /> Mark Complete</Btn>}
          </div>
        } />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {m ? (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ backgroundColor: `${m.color}1F`, color: m.color }} data-testid="task-detail-status-chip">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.color }} />{m.label}
          </span>
        ) : <Badge value={task.status} />}
        <Badge value={task.priority} />
        {overdue && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2.5 py-1 text-[10px] font-bold text-[#C62828]" data-testid="task-detail-overdue-chip"><Flame size={10} /> {overdueDays} DAY{overdueDays === 1 ? "" : "S"} OVERDUE</span>}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="task-detail-stats">
        <StatCard label="Time tracked" value={<span className="inline-flex items-center gap-2 tabular-nums">{fmtHMS(task.actual_seconds || 0)}
          <button data-testid="task-detail-timer-btn" aria-label={task.timer_started_at ? "Stop timer" : "Start timer"} onClick={timer}
            className={`flex h-7 w-7 items-center justify-center rounded-full ${task.timer_started_at ? "bg-[#2E7D32] text-white" : "bg-[#F5F2EB] text-[#4E342E] hover:bg-[#EDE7DC]"}`}>
            {task.timer_started_at ? <Square size={10} /> : <Play size={11} />}
          </button></span>} sub={task.timer_started_at ? "Timer running" : "Timer stopped"} />
        <StatCard label="Days open" value={daysOpen} sub={`since ${dayjs(task.created_at).format("MMM D")}`} />
        <StatCard label="Comments" value={(task.comments || []).length} />
        <StatCard label="Activity events" value={activity === null ? "…" : activity.length} />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <Card className="p-5" data-testid="task-detail-info">
            <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">Details</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {details.map(([l, v]) => (
                <div key={l}><div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{l}</div><div className="text-[#3E2723]">{v}</div></div>
              ))}
            </div>
            {task.summary && (
              <div className="mt-4 rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm leading-relaxed text-[#4E342E]" data-testid="task-detail-summary">{task.summary.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</div>
            )}
          </Card>
          <Card className="p-5" data-testid="task-detail-comments">
            <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><MessageSquare size={14} /> Comments ({(task.comments || []).length})</h3>
            <div className="mb-3 space-y-2">
              {(task.comments || []).map((c, i) => (
                <div key={i} className="rounded-xl bg-[#F5F2EB] px-4 py-2.5 text-sm">
                  <span className="font-semibold text-[#3E2723]">{c.by}</span>
                  <span className="ml-2 text-xs text-[#84675B]">{dayjs(c.at).format("MM/DD HH:mm")}</span>
                  <p className="mt-0.5 text-[#4E342E]">{c.text}</p>
                </div>
              ))}
              {(task.comments || []).length === 0 && <p className="text-xs text-[#84675B]">No comments yet.</p>}
            </div>
            <div className="flex gap-2">
              <input data-testid="task-detail-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…"
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                className="h-10 flex-1 rounded-lg border border-[#E5DFD3] px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" />
              <Btn data-testid="task-detail-comment-send" className="rounded-lg shrink-0" onClick={addComment}><Send size={14} /></Btn>
            </div>
          </Card>
        </div>
        <Card className="p-5" data-testid="task-detail-activity">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><History size={14} /> Activity & Audit Trail</h3>
          {activity === null ? <div className="py-8 text-center text-sm text-[#84675B]">Loading…</div> :
            activity.length === 0 ? <Empty text="No recorded activity yet" /> : (
              <div className="relative space-y-3 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-[#E5DFD3]">
                {activity.map((e, i) => (
                  <div key={i} className="relative pl-6" data-testid={`task-detail-event-${i}`}>
                    <span className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${e.action === "create" ? "bg-[#2E7D32]" : e.action === "comment" ? "bg-[#1565C0]" : e.action === "sign" ? "bg-[#5E35B1]" : "bg-[#8D6E63]"}`} />
                    <div className="text-sm"><span className="font-semibold capitalize text-[#3E2723]">{e.action}</span><span className="text-[#5D4037]"> by {e.by}</span></div>
                    {e.detail && <div className="text-xs text-[#5D4037]">{e.detail}</div>}
                    <div className="flex items-center gap-1 text-[11px] text-[#84675B]"><Clock3 size={10} />{dayjs(e.at).format("MMM D, YYYY h:mm A")}</div>
                  </div>
                ))}
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
